import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Icon, Container, Header, Segment, Grid,
    Button, Divider, Message, Label, Transition, Placeholder
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { MediaTile } from '../../../../components/MediaTile';
import { useContentReviewerGate } from '../../../../hooks/isContentReviewer';
import { PATHS } from '../../../../paths';
import {
    FILE_INPUT_ACCEPT, MAX_VIDEO_DURATION_SECONDS, putContentTypeForFile,
    uploadContentTypeForFile, validateMediaFiles,
} from '../../../../utils/media';
import styles from './AllGalleryView.module.css';

const GET_IMAGES = gql`
  query ImagesConnection($cursor: String, $limit: Int) {
    imagesConnection(cursor: $cursor, limit: $limit) {
      items { id, url, contentType, durationSeconds }
      nextCursor
      hasNextPage
    }
  }
`;

const GET_SINGLE_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      contentType
      durationSeconds
    }
  }
`;

const CREATE_IMAGE_UPLOAD = gql`
  mutation CreateImageUpload($fileName: String!, $contentType: String, $durationSeconds: Float) {
    createImageUpload(fileName: $fileName, contentType: $contentType, durationSeconds: $durationSeconds) {
      image { id }
      uploadUrl
    }
  }
`;

/** A picked file plus the duration we probed while validating it, which the backend re-checks. */
interface StagedFile {
    file: File;
    durationSeconds?: number;
    /** Object URL for the preview. Held so it can be revoked on unstage/unmount. */
    previewUrl: string;
}

// --- Small helper for the top selection bar ---
const SelectedThumbnail = ({ id, onRemove }: { id: number, onRemove: (id: number) => void }) => {
    const { data, loading } = useQuery(GET_SINGLE_IMAGE, { variables: { imageId: id } });

    if (loading) return <Placeholder style={{ height: 60, width: 60, borderRadius: '8px', margin: '0 5px' }}><Placeholder.Image /></Placeholder>;
    if (!data?.image) return null;

    return (
        <div style={{ position: 'relative', display: 'inline-block', margin: '0 8px' }}>
            <MediaTile
                url={data.image.url}
                contentType={data.image.contentType}
                durationSeconds={data.image.durationSeconds}
                style={{ width: '60px', height: '60px', objectFit: 'cover', border: '2px solid #2185d0' }}
                rounded
            />
            <Button
                icon="close" size="mini" color="red" circular
                onClick={() => onRemove(id)}
                style={{ position: 'absolute', top: '-5px', right: '-5px', padding: '3px', fontSize: '8px', zIndex: 5 }}
            />
        </div>
    );
};

const AllGalleryView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();

    // Initialize from URL
    const [selectedImageIds, setSelectedImageIds] = useState<number[]>(() => {
        const params = new URLSearchParams(location.search);
        const ids = params.get('images');
        return ids ? ids.split(',').map(Number).filter(Boolean) : [];
    });

    const [showUpload, setShowUpload] = useState(false);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);

    // Preview object URLs are revoked on unstage and after upload; this catches the case
    // where the user navigates away with files still staged.
    const stagedFilesRef = useRef(stagedFiles);
    stagedFilesRef.current = stagedFiles;
    useEffect(() => () => {
        stagedFilesRef.current.forEach(staged => URL.revokeObjectURL(staged.previewUrl));
    }, []);

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (selectedImageIds.length > 0) {
            params.set('images', selectedImageIds.join(','));
        } else {
            params.delete('images');
        }
        navigate({ search: params.toString() }, { replace: true });
    }, [selectedImageIds, navigate, location.search]);

    const { loading, error, data, fetchMore, refetch } = useQuery(GET_IMAGES, {
        variables: { limit: 12, cursor: null },
    });

    const [createImageUpload] = useMutation(CREATE_IMAGE_UPLOAD);

    const toggleImageSelection = (id: number) => {
        setSelectedImageIds(prev =>
            prev.includes(id) ? prev.filter(imageId => imageId !== id) : [...prev, id]
        );
    };

    const handleConfirmSelection = () => {
        navigate(`/posts/compose?images=${selectedImageIds.join(',')}`);
    };

    /**
     * Single funnel for both the file input and the drop zone. Videos need a duration probe
     * (and a size/format check) before they can be staged, so this is async — files that
     * pass are staged, the rest are reported.
     */
    const stageFiles = async (incoming: File[]) => {
        if (incoming.length === 0) return;

        const { accepted, errors } = await validateMediaFiles(incoming);
        setUploadErrors(errors);
        if (accepted.length === 0) return;

        setStagedFiles(prev => [
            ...prev,
            ...accepted.map(({ file, durationSeconds }) => ({
                file,
                durationSeconds,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);
    };

    const unstageFile = (index: number) => {
        setStagedFiles(prev => {
            const target = prev[index];
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const clearStagedFiles = () => {
        setStagedFiles(prev => {
            prev.forEach(staged => URL.revokeObjectURL(staged.previewUrl));
            return [];
        });
    };

    const performUpload = async () => {
        setIsUploading(true);
        setUploadErrors([]);
        const newImageIds = []
        try {
            for (const { file, durationSeconds } of stagedFiles) {
                const { data: uploadResponse } = await createImageUpload({
                    variables: {
                        fileName: file.name,
                        contentType: uploadContentTypeForFile(file),
                        durationSeconds,
                    },
                });
                if (uploadResponse?.createImageUpload) {
                    const { uploadUrl, image } = uploadResponse.createImageUpload;
                    const { id } = image;
                    const response = await fetch(uploadUrl, {
                        method: 'PUT',
                        body: file,
                        // This header is what sets the stored object's content type — the
                        // presign uses SignedHeaders=host, so the backend's ContentType is
                        // never applied. Videos stored as octet-stream won't play.
                        headers: { 'Content-Type': putContentTypeForFile(file) },
                    });
                    // Without this an S3 rejection looks like a successful upload, and the
                    // Image row the backend already created is left pointing at nothing.
                    if (!response.ok) {
                        throw new Error(`Upload of ${file.name} failed (${response.status}).`);
                    }
                    newImageIds.push(id);
                }
            }
            clearStagedFiles();
            setShowUpload(false);

            // reroute to a chain of tagging
            // TAG_IMAGE: "/gallery/:imageId/tags",
            // gallery/1/tags?nextImageIds=2,3,4 so when i am done with this it will go to the next image
            let path = '';
            if (newImageIds.length > 0) {
                path = `/gallery/${newImageIds[0]}/tags`
            }

            if (newImageIds.length > 1) {
                newImageIds.shift()
                path = `${path}?nextImageIds=${newImageIds.join(',')}`
            }

            if (path.length === 0) {
                refetch()
            } else {
                navigate(path)
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setUploadErrors([err instanceof Error ? err.message : 'Upload failed. Please try again.']);
        } finally {
            setIsUploading(false);
        }
    };

    // Content reviewers only — everyone else goes back to classes.
    // Wait for GET_ME so a real reviewer isn't bounced during the initial load.
    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    return (
        <ResponsiveContainer>
            <Segment basic style={{ padding: '2em 0em' }}>
                <Container>
                    {/* Selection Highlighting Area */}
                    <Transition visible={selectedImageIds.length > 0} animation='fade down' duration={300}>
                        <Segment color="blue" raised style={{ marginBottom: '2em', background: '#f0f7ff', position: 'sticky', top: '64px', zIndex: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', overflowX: 'auto', padding: '10px 0', flex: 1 }}>
                                    {selectedImageIds.map(id => (
                                        <SelectedThumbnail key={id} id={id} onRemove={toggleImageSelection} />
                                    ))}
                                </div>
                                <div style={{ paddingLeft: '20px' }}>
                                    <Button color="blue" size="large" onClick={handleConfirmSelection} icon labelPosition="right">
                                        Confirm {selectedImageIds.length} Items
                                        <Icon name='arrow right' />
                                    </Button>
                                    <Button icon="trash" basic color="red" circular onClick={() => setSelectedImageIds([])} style={{ marginLeft: '10px' }} />
                                </div>
                            </div>
                        </Segment>
                    </Transition>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Header as='h2' style={{ margin: 0 }}>
                            <Icon name='arrow left' circular link onClick={() => navigate(-1)} />
                            <Header.Content>
                                Gallery
                                <Header.Subheader>Select images in the order you want them to appear</Header.Subheader>
                            </Header.Content>
                        </Header>

                        <Button
                            color={showUpload ? "black" : "grey"}
                            icon labelPosition="left"
                            onClick={() => setShowUpload(!showUpload)}
                        >
                            <Icon name={showUpload ? "chevron up" : "upload"} />
                            {showUpload ? "Close Upload" : "Upload New"}
                        </Button>
                    </div>

                    <Transition visible={showUpload} animation='slide down' duration={300}>
                        <Segment secondary padded style={{ marginTop: '2em', borderRadius: '12px' }}>
                            <div
                                className={styles.dropZone}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (e.dataTransfer.files) stageFiles(Array.from(e.dataTransfer.files));
                                }}
                            >
                                <Header icon color="grey">
                                    <Icon name='cloud upload' />
                                    Drag and drop photos or videos here
                                    <Header.Subheader style={{ marginTop: '0.5em', fontWeight: 'normal' }}>
                                        Videos must be {MAX_VIDEO_DURATION_SECONDS} seconds or shorter (MP4 or MOV)
                                    </Header.Subheader>
                                    <Divider horizontal>OR</Divider>
                                    <Button as="label" htmlFor="file-input" basic color="blue">
                                        Select Documents
                                        <input
                                            id="file-input"
                                            type="file"
                                            hidden
                                            multiple
                                            accept={FILE_INPUT_ACCEPT}
                                            onChange={(e) => {
                                                const picked = Array.from(e.target.files ?? []);
                                                // Reset so re-picking a file that was unstaged still fires onChange.
                                                e.target.value = '';
                                                stageFiles(picked);
                                            }}
                                        />
                                    </Button>
                                </Header>
                            </div>

                            {uploadErrors.length > 0 && (
                                <Message
                                    warning
                                    onDismiss={() => setUploadErrors([])}
                                    header={uploadErrors.length > 1 ? "Some files couldn't be added" : "That file couldn't be added"}
                                    list={uploadErrors}
                                />
                            )}

                            {stagedFiles.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                                        {stagedFiles.map((staged, index) => (
                                            <div key={staged.previewUrl} style={{ position: 'relative' }}>
                                                <MediaTile
                                                    url={staged.previewUrl}
                                                    contentType={staged.file.type}
                                                    durationSeconds={staged.durationSeconds}
                                                    alt="preview"
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                                <Button icon='delete' size='mini' color='red' circular style={{ position: 'absolute', top: '-8px', right: '-8px' }} onClick={() => unstageFile(index)} />
                                            </div>
                                        ))}
                                    </div>
                                    <Button primary fluid size="large" loading={isUploading} onClick={performUpload}>
                                        Upload {stagedFiles.length} {stagedFiles.length === 1 ? 'Item' : 'Items'}
                                    </Button>
                                </div>
                            )}
                        </Segment>
                    </Transition>

                    <Divider section />

                    {error && <Message error content={error.message} />}

                    <Grid doubling stackable columns={4}>
                        {data?.imagesConnection.items.map((img: any) => {
                            const selectedIndex = selectedImageIds.indexOf(img.id);
                            const isSelected = selectedIndex !== -1;
                            return (
                                <Grid.Column key={img.id}>
                                    <div
                                        className={`${styles.imageWrapper} ${isSelected ? styles.selected : ''}`}
                                        onClick={() => toggleImageSelection(img.id)}
                                        style={{ cursor: 'pointer', position: 'relative' }}
                                    >
                                        <MediaTile
                                            url={img.url}
                                            contentType={img.contentType}
                                            durationSeconds={img.durationSeconds}
                                            fluid
                                            rounded
                                            style={{ border: isSelected ? '4px solid #2185d0' : '1px solid #ddd', transition: 'all 0.2s' }}
                                        />

                                        {/* Selection Number Overlay */}
                                        {isSelected && (
                                            <Label color="blue" floating circular style={{ top: '10px', left: '10px', zIndex: 10 }}>
                                                {selectedIndex + 1}
                                            </Label>
                                        )}

                                        {/* Edit Button Overlay (Visible on Hover via CSS) */}
                                        <div className={styles.editButtonOverlay}>
                                            <Button
                                                as={Link}
                                                to={`/gallery/${img.id}/tags`}
                                                icon
                                                circular
                                                color="blue"
                                                size="small"
                                                onClick={(e: any) => e.stopPropagation()} // Important: stop click from selecting image
                                                style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 12 }}
                                            >
                                                <Icon name="pencil" />
                                            </Button>
                                        </div>

                                        {/* Checkmark Overlay (Visible only when selected) */}
                                        {isSelected && (
                                            <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
                                                <Icon name="check circle" size="large" color="blue" />
                                            </div>
                                        )}
                                    </div>
                                </Grid.Column>
                            );
                        })}
                    </Grid>

                    {data?.imagesConnection.hasNextPage && (
                        <div style={{ textAlign: 'center', marginTop: '2em' }}>
                            <Button basic color="blue" loading={loading} onClick={() => fetchMore({ variables: { cursor: data.imagesConnection.nextCursor }, updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult ? { imagesConnection: { ...fetchMoreResult.imagesConnection, items: [...prev.imagesConnection.items, ...fetchMoreResult.imagesConnection.items] } } : prev })} content="Load More" />
                        </div>
                    )}
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { AllGalleryView };
